import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useRecordNav from '../../hooks/useRecordNav';
import RecordNav from '../../components/RecordNav';
import { supabase } from '../../lib/supabase';
import ProfileTabBar from '../../components/ProfileTabBar';
import { useRole } from '../../components/RoleGuard';
import { useAuth } from '../../hooks/useAuth';
import { getStaffPerformanceReviews, REVIEW_TYPE_LABEL, REVIEW_COMPETENCIES } from '../../lib/performance';
import { certificationStatus } from '../../lib/training';
import { getRole, ROLES, getOfferLetterHtml, getJobDescriptionHtml, TEMPLATE_CODE_TO_POSITION, BACKGROUND_CHECK_CONSENT, ORIENTATION_CHECKLIST_ITEMS } from '../../lib/onboardingTemplates';
import { requestStaffDocument } from '../../lib/staffDocuments';
import { canReviseSigned } from '../../lib/offerRevision';
import ReviseOfferModal from '../../components/hr/ReviseOfferModal';
import PayRateHistory from '../../components/hr/PayRateHistory';
import { ClinicalCertsPanel, BackgroundCheckPanel, ScreeningChecksPanel } from '../../components/hr/StaffCompliancePanels';
import RecordCheckModal from '../../components/hr/RecordCheckModal';
import StaffFilesPanel from '../../components/hr/StaffFilesPanel';
import { SCREENING_KINDS, EXCLUSION_STALE_DAYS, screeningKindsFor, screeningState } from '../../lib/screeningChecks';
import { parseLocalDate } from '../../lib/dateUtils';
import { getStaffSupervisionHistory, KIND_LABEL as SUPERVISION_KIND } from '../../lib/supervision';
import { format, differenceInDays } from 'date-fns';
import { RESTRICTION_OPTIONS } from '../../lib/pets';

// Retroactive document requests (2026-08-08) — for a staff member who is
// missing a signable document (offer letter, job description, background
// check consent, or the orientation checklist), whether because they were
// fast-tracked in via Personnel.js's Quick Add or simply never sent it
// during the normal Onboarding Pipeline flow. Reuses the exact same
// staff_documents/HRDocSign.js signing path the pipeline uses — see
// requestStaffDocument (src/lib/staffDocuments.js).
// A candidate who has not been sent the offer pack yet gets these from the
// pack in Onboarding, on one link — never on their own from here. An offer
// letter sent alone from this checklist blocked the whole pack for Bolatito
// Adenuga (2026-10-19). Staff already working keep the single send.
const PACK_DOC_TYPES = ['offer_letter', 'job_description', 'background_check_consent'];

const DOC_TYPE_FOR_CHECKLIST_KEY = {
  offer_letter: 'offer_letter',
  job_description: 'job_description',
  bg_check_consent: 'background_check_consent',
  orientation: 'orientation_checklist',
};

function RequestDocModal({ staff, docType, docLabel, officeSignedBy, officeSignerRole, onClose, onSent }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [roleCode, setRoleCode] = useState(staff.hire_role_code || '');
  const [checklist, setChecklist] = useState({});
  const lang = staff.preferred_language === 'es' ? 'es' : 'en';

  const needsRolePicker = docType === 'offer_letter' || docType === 'job_description';
  const eligibleRoles = docType === 'offer_letter'
    ? ROLES.filter(r => r.offerLetters?.[staff.employment_type])
    : docType === 'job_description'
      ? ROLES.filter(r => r.jobDescription)
      : [];
  const noEligibleRole = needsRolePicker && eligibleRoles.length === 0;
  const allAnswered = docType !== 'orientation_checklist' || ORIENTATION_CHECKLIST_ITEMS.every(item => checklist[item.key]);
  const packFirst = staff.onboarding_status === 'candidate' && PACK_DOC_TYPES.includes(docType);
  const canSend = !busy && !packFirst && !noEligibleRole && (!needsRolePicker || roleCode) && allAnswered;

  const send = async () => {
    setBusy(true); setError('');
    try {
      let content = null;
      let label = docLabel;
      if (docType === 'offer_letter') {
        const startDateStr = staff.start_date ? format(parseLocalDate(staff.start_date), 'MMMM d, yyyy') : 'a mutually agreed date';
        const acceptBy = new Date(); acceptBy.setDate(acceptBy.getDate() + 7);
        content = getOfferLetterHtml(roleCode, staff.employment_type, { full_name: staff.full_name, pay_rate: staff.pay_rate }, { startDateStr, acceptByStr: format(acceptBy, 'MMMM d, yyyy') }, lang);
        if (!content) throw new Error('No offer letter template for this role/employment type combination.');
      } else if (docType === 'job_description') {
        content = getJobDescriptionHtml(roleCode, lang);
        if (!content) throw new Error('No job description template for this role.');
      } else if (docType === 'background_check_consent') {
        const c = BACKGROUND_CHECK_CONSENT[lang] || BACKGROUND_CHECK_CONSENT.en;
        content = c.content; label = c.doc_label;
      } else if (docType === 'orientation_checklist') {
        const rows = ORIENTATION_CHECKLIST_ITEMS.map(item => {
          const answer = checklist[item.key] === 'na' ? 'N/A' : 'Completed';
          return `<li><strong>${item.label}</strong>${item.ref ? ` (${item.ref})` : ''} — ${answer}</li>`;
        }).join('');
        content = `<p>This checklist confirms the topics and steps below were reviewed as part of ${staff.full_name}'s Orientation &amp; Training, per Policy A-2.2.</p><ul>${rows}</ul><p>By signing below, I confirm I participated in this orientation and understand the topics reviewed.</p>`;
      }
      // Persist the chosen role template so the record stays consistent
      // going forward — same fields sendOffer sets in OnboardingPipeline.js.
      if (needsRolePicker && roleCode && roleCode !== staff.hire_role_code) {
        const resolvedPosition = TEMPLATE_CODE_TO_POSITION[roleCode] || {};
        await supabase.from('staff').update({ hire_role_code: roleCode, ...resolvedPosition }).eq('id', staff.id);
      }
      await requestStaffDocument(staff, docType, { docLabel: label, content, officeSignedBy, officeSignerRole, lang });
      onSent();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">Send {docLabel}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
          {!staff.email && <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--warn)' }}>No email on file — add one before sending.</div>}
          {packFirst && (
            <div style={{ background: 'var(--accent-bg)', border: '0.5px solid var(--accent-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.5 }}>
              {staff.first_name || staff.full_name} is still a candidate, so the {(docLabel || 'document').toLowerCase()} goes out in the <b>offer pack</b> from Onboarding, together with the other documents, on one link. Sending it on its own here would stop the pack from going.
              <div style={{ marginTop: 8 }}><Link to="/hr/onboarding" className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>Open Onboarding</Link></div>
            </div>
          )}

          {needsRolePicker && (
            noEligibleRole ? (
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>No {docType === 'offer_letter' ? 'offer letter' : 'job description'} template applies to this role/employment type combination.</div>
            ) : (
              <div className="form-group">
                <label className="form-label">Role Template</label>
                <select className="form-select" value={roleCode} onChange={e => setRoleCode(e.target.value)}>
                  <option value="">Select...</option>
                  {eligibleRoles.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                </select>
              </div>
            )
          )}

          {docType === 'background_check_consent' && (
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: (BACKGROUND_CHECK_CONSENT[lang] || BACKGROUND_CHECK_CONSENT.en).content }} />
          )}

          {docType === 'orientation_checklist' && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>Mark every topic Completed or N/A before sending — this becomes the record {staff.full_name} signs.</div>
              {ORIENTATION_CHECKLIST_ITEMS.map(item => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 12, color: 'var(--text)' }}>{item.label}{item.ref ? <span style={{ color: 'var(--text3)' }}> ({item.ref})</span> : ''}</span>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {['completed', 'na'].map(opt => (
                      <button key={opt}
                        className="btn btn-sm"
                        style={{ fontSize: 10, padding: '4px 8px', background: checklist[item.key] === opt ? 'var(--accent)' : 'var(--bg2)', color: checklist[item.key] === opt ? '#fff' : 'var(--text3)' }}
                        onClick={() => setChecklist(p => ({ ...p, [item.key]: opt }))}>
                        {opt === 'completed' ? 'Completed' : 'N/A'}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={!canSend || !staff.email}>{busy ? 'Sending…' : 'Send'}</button>
        </div>
      </div>
    </div>
  );
}

// Grant CAERA login (2026-08-31) — closes the gap where `invite-staff` creates
// auth.users + profiles but knows nothing about the `staff` table, so a
// personnel record stayed "No login" forever even after the person could sign
// in. Previously the only code path that wrote staff.profile_id was the
// Caregiver App room invite (core/CaregiverApp.js). This does both halves —
// invite *and* link — in one admin action, and also repairs records whose
// account already exists but was never linked.
const CAERA_ROLES = ['admin', 'manager', 'coordinator', 'nurse', 'staff'];

// Starting CAERA role suggested from the personnel position code. Only a
// default — the admin picks the final value in the dropdown.
const DEFAULT_CAERA_ROLE = {
  RN: 'nurse', LPN: 'nurse',
  PCC: 'coordinator', BOC: 'coordinator',
  MGR: 'manager', DIR_OPS: 'manager',
};

function GrantLoginModal({ staff, onClose, onDone }) {
  const email = (staff.email || '').trim();
  const [role, setRole] = useState(DEFAULT_CAERA_ROLE[staff.position_code] || 'staff');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [existing, setExisting] = useState(undefined); // undefined = still checking, null = none
  const [result, setResult] = useState(null);          // { tempPassword? , linkedOnly }

  // An account may already exist for this email (invited from Settings → User
  // Management, which doesn't link back). In that case link it instead of
  // re-inviting — invite-staff rejects duplicate emails outright.
  useEffect(() => {
    if (!email) { setExisting(null); return; }
    let cancelled = false;
    supabase.from('profiles').select('id, full_name, role, email').ilike('email', email).maybeSingle()
      .then(({ data }) => { if (!cancelled) setExisting(data || null); });
    return () => { cancelled = true; };
  }, [email]);

  const linkProfile = async (profileId) => {
    const { data: clash } = await supabase.from('staff').select('id, full_name').eq('profile_id', profileId).maybeSingle();
    if (clash && clash.id !== staff.id) {
      throw new Error(`That account is already linked to ${clash.full_name}'s personnel record. Unlink it there first.`);
    }
    const { error: err } = await supabase.from('staff').update({ profile_id: profileId }).eq('id', staff.id);
    if (err) throw new Error(`Account is ready but linking it to this personnel record failed: ${err.message}`);
  };

  const submit = async () => {
    setBusy(true); setError('');
    try {
      if (existing) {
        await linkProfile(existing.id);
        setResult({ linkedOnly: true });
      } else {
        const { data, error: fnError } = await supabase.functions.invoke('invite-staff', {
          body: { action: 'invite', full_name: staff.full_name, email, role },
        });
        if (fnError) throw new Error(fnError.message);
        if (data?.error) throw new Error(data.error);
        const { data: newProfile } = await supabase.from('profiles').select('id').ilike('email', email).maybeSingle();
        if (!newProfile) throw new Error('Account was created but could not be found to link. Link it from Settings → User Management.');
        await linkProfile(newProfile.id);
        setResult({ tempPassword: data?.tempPassword, linkedOnly: false });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && (result ? onDone() : onClose())}>
      <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <div className="modal-title">{result ? 'CAERA Login Ready' : 'Grant CAERA Login'}</div>
          <button className="btn btn-ghost btn-sm" onClick={() => result ? onDone() : onClose()}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}

          {result ? (
            <>
              <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--success)' }}>
                {result.linkedOnly
                  ? `${staff.full_name}'s existing CAERA account is now linked to this personnel record.`
                  : `${staff.full_name} can now sign in. A welcome email with their temporary password was sent to ${email}.`}
              </div>
              {result.tempPassword && (
                <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                  <div style={{ marginBottom: 6 }}>Temporary password (in case the email doesn't arrive):</div>
                  <code style={{ display: 'inline-block', background: 'var(--bg2)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '6px 10px', fontSize: 13, color: 'var(--text)' }}>{result.tempPassword}</code>
                  <div style={{ marginTop: 10, color: 'var(--text3)' }}>They'll be asked to change it after their first sign-in at {window.location.origin}/login.</div>
                </div>
              )}
            </>
          ) : (
            <>
              {!email && <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--warn)' }}>No email on file — add a work email to this personnel record first.</div>}

              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14 }}>
                <div><strong style={{ color: 'var(--text)' }}>{staff.full_name}</strong></div>
                <div style={{ color: 'var(--text3)' }}>{email || '—'}</div>
              </div>

              {existing === undefined && email && <div style={{ fontSize: 12, color: 'var(--text3)' }}>Checking for an existing account…</div>}

              {existing && (
                <div style={{ background: 'var(--info-bg)', border: '0.5px solid var(--info-border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--info)' }}>
                  A CAERA account already exists for this email ({existing.role}). Linking it to this personnel record — no new invite is sent and their password is unchanged. Change their role or module access in Settings → User Management.
                </div>
              )}

              {existing === null && email && (
                <>
                  <div className="form-group">
                    <label className="form-label">CAERA Role</label>
                    <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
                      {CAERA_ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                    </select>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                    Creates their account, emails a temporary password, and links it to this personnel record. Module access follows the role defaults — fine-tune it afterwards in Settings → User Management.
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          {result ? (
            <button className="btn btn-primary" onClick={onDone}>Done</button>
          ) : (
            <>
              <button className="btn btn-outline" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={submit} disabled={busy || !email || existing === undefined}>
                {busy ? (existing ? 'Linking…' : 'Creating…') : (existing ? 'Link Account' : 'Create Login & Send Invite')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'8px 0', borderBottom:'1px solid var(--border)', gap:12 }}>
    <span style={{ fontSize:12, color:'var(--text3)', flexShrink:0, width:150 }}>{label}</span>
    <span style={{ fontSize:13, color:'var(--text)', fontWeight:500, textAlign:'right' }}>{value ?? '—'}</span>
  </div>
);

const Section = ({ title, children, action }) => (
  <div className="card" style={{ marginBottom:12, border:'0.5px solid var(--accent-border)' }}>
    <div style={{ padding:'10px 16px', borderBottom:'0.5px solid var(--accent-border)', background:'var(--accent-bg)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
      <div style={{ fontSize:11, fontWeight:600, color:'var(--accent)', textTransform:'uppercase', letterSpacing:'0.07em' }}>{title}</div>
      {action}
    </div>
    <div style={{ padding:'0 16px' }}>{children}</div>
  </div>
);

const pill = (label, style) => (
  <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, whiteSpace:'nowrap', ...style }}>{label}</span>
);

const roleColor = code => ({
  RN:      { bg: 'var(--accent-bg)',  color: 'var(--accent2)' },
  LPN:     { bg: 'var(--accent-bg)',  color: 'var(--accent2)' },
  PCA:     { bg: 'var(--success-bg)', color: 'var(--success)' },
  PCC:     { bg: 'var(--info-bg)',    color: 'var(--info)' },
  DIR_OPS: { bg: 'var(--copper-bg)',  color: 'var(--copper)' },
  BOC:     { bg: 'var(--warn-bg)',    color: 'var(--warn)' },
  MGR:     { bg: 'var(--copper-bg)',  color: 'var(--copper)' },
}[code] || { bg: 'var(--bg2)', color: 'var(--text3)' });

const statusColor = s => ({
  active:     { bg: 'var(--success-bg)', color: 'var(--success)' },
  inactive:   { bg: 'var(--bg2)',        color: 'var(--text3)' },
  on_leave:   { bg: 'var(--warn-bg)',    color: 'var(--warn)' },
  terminated: { bg: 'var(--danger-bg)',  color: 'var(--danger)' },
}[s] || { bg: 'var(--bg2)', color: 'var(--text3)' });

const docStatusColor = s => ({
  pending:  { bg: 'var(--warn-bg)',    color: 'var(--warn)' },
  sent:     { bg: 'var(--info-bg)',    color: 'var(--info)' },
  signed:   { bg: 'var(--success-bg)', color: 'var(--success)' },
  declined: { bg: 'var(--danger-bg)',  color: 'var(--danger)' },
  on_file:  { bg: 'var(--bg2)',        color: 'var(--text2)' },
}[s] || { bg: 'var(--bg2)', color: 'var(--text3)' });

const courseStatusColor = s => ({
  assigned:    { bg: 'var(--warn-bg)',    color: 'var(--warn)' },
  in_progress: { bg: 'var(--info-bg)',    color: 'var(--info)' },
  completed:   { bg: 'var(--success-bg)', color: 'var(--success)' },
}[s] || { bg: 'var(--bg2)', color: 'var(--text3)' });

const incidentStatusColor = s => ({
  open:   { bg: 'var(--warn-bg)',    color: 'var(--warn)' },
  closed: { bg: 'var(--success-bg)', color: 'var(--success)' },
}[s] || { bg: 'var(--bg2)', color: 'var(--text3)' });

const applicationStatusColor = s => ({
  pending:   { bg: 'var(--warn-bg)',    color: 'var(--warn)' },
  converted: { bg: 'var(--success-bg)', color: 'var(--success)' },
  rejected:  { bg: 'var(--danger-bg)',  color: 'var(--danger)' },
}[s] || { bg: 'var(--bg2)', color: 'var(--text3)' });

const reviewStatusColor = s => ({
  draft:        { bg: 'var(--bg2)',        color: 'var(--text3)' },
  submitted:    { bg: 'var(--info-bg)',    color: 'var(--info)' },
  acknowledged: { bg: 'var(--success-bg)', color: 'var(--success)' },
}[s] || { bg: 'var(--bg2)', color: 'var(--text3)' });

const fmtDate = (d, withTime) => d ? format(parseLocalDate(d), withTime ? 'MMM d, yyyy h:mm a' : 'MMM d, yyyy') : null;

const expiryValue = (dateStr) => {
  if (!dateStr) return null;
  const days = differenceInDays(parseLocalDate(dateStr), new Date());
  const formatted = format(parseLocalDate(dateStr), 'MMM d, yyyy');
  if (days <= 60) return <span style={{ color:'var(--danger)', fontWeight:600 }}>{formatted} ⚠</span>;
  return formatted;
};

const payRate = (s) => s.pay_rate
  ? `$${Number(s.pay_rate).toFixed(2)}${{ hourly:'/hr', weekly:'/wk', bi_weekly:'/2wk', monthly:'/mo', annually:'/yr', per_visit:'/visit' }[s.pay_type] || ''}`
  : null;

// Personnel-file completeness checklist — added after the 2026-07-15 process
// review flagged there was no single place to see what's actually complete
// vs. missing per employee (had to check the Onboarding Pipeline stage AND
// three separate PersonnelProfile tabs separately). 'na' items don't count
// against completeness (e.g. no offer letter template exists for this role,
// or this staff member has no application because they came from AxisCare).
const CHECKLIST_STATE = {
  complete: { label: 'Complete', icon: '✓', bg: 'var(--success-bg)', color: 'var(--success)' },
  pending:  { label: 'Pending',  icon: '⏳', bg: 'var(--warn-bg)',    color: 'var(--warn)' },
  missing:  { label: 'Missing',  icon: '✗', bg: 'var(--danger-bg)',  color: 'var(--danger)' },
  flagged:  { label: 'Flagged',  icon: '⚠', bg: 'var(--danger-bg)',  color: 'var(--danger)' },
  na:       { label: 'N/A',      icon: '—', bg: 'var(--bg2)',        color: 'var(--text3)' },
  external: { label: 'External', icon: 'ℹ', bg: 'var(--info-bg)',    color: 'var(--info)' },
};

function buildPersonnelChecklist(staff, application, documents, certifications, courseRecords) {
  const findDoc = (type) => documents.find(d => d.doc_type === type);
  const role = getRole(staff.hire_role_code);
  const findCert = (type) => certifications.find(c => c.cert_type === type);
  const findCourse = (code) => courseRecords.find(r => r.course?.code === code);

  const items = [];

  // Staff added via AxisCare sync or manual entry never went through the
  // Onboarding Pipeline's application → offer → job-description flow at all —
  // there's nothing to sign because nothing was ever sent. Same N/A logic the
  // application item already used; offer_letter/job_description previously
  // didn't apply it and permanently showed "Missing" for every such staff
  // member (38 of 42 active staff as of the audit that caught this).
  const noApplicationFlow = staff.source === 'manual' || staff.source === 'axiscare';

  // Application
  if (!noApplicationFlow && !application) {
    items.push({ key: 'application', label: 'Application on file', state: 'missing', detail: 'No application record found.' });
  } else if (application) {
    items.push({ key: 'application', label: 'Application on file', state: 'complete', detail: `Submitted ${fmtDate(application.created_at)}` });
  } else {
    items.push({ key: 'application', label: 'Application on file', state: 'na', detail: staff.source === 'axiscare' ? 'Added via AxisCare sync' : 'Added manually — no application expected' });
  }

  // Offer letter. A revised letter is a second row (offerRevision.js), so read
  // the newest, and mention the first signed one once there is a revision.
  const offerDocs = documents.filter(d => d.doc_type === 'offer_letter' && d.status !== 'withdrawn');
  const offerDoc = offerDocs[offerDocs.length - 1];
  const originalOffer = offerDocs.length > 1 ? offerDocs.find(d => d.status === 'signed') : null;
  const originalNote = originalOffer && originalOffer !== offerDoc ? ` · original signed ${fmtDate(originalOffer.signed_at)}` : '';
  // Checked for this role AND this employment type. It used to ask only
  // whether the role had any letter at all, so a W2 LPN (1099 letter only)
  // showed "Missing" with a Send request button that could not produce one.
  // Every role now carries both, so this is a guard for roles added later.
  const isAgencyPlacement = staff.employment_type === 'agency' || staff.hire_track === 'agency';
  const offerLetterExpected = role ? !!role.offerLetters?.[staff.employment_type] : true;
  if (!offerDoc && isAgencyPlacement) {
    items.push({ key: 'offer_letter', label: 'Offer Letter signed', state: 'na', detail: 'Agency placement — employed by the sending agency, so no offer letter (Policy A-2.4)' });
  } else if (!offerDoc && noApplicationFlow) {
    items.push({ key: 'offer_letter', label: 'Offer Letter signed', state: 'na', detail: staff.source === 'axiscare' ? 'Added via AxisCare sync — no offer letter expected' : 'Added manually — no offer letter expected' });
  } else if (!offerLetterExpected) {
    items.push({ key: 'offer_letter', label: 'Offer Letter signed', state: 'missing', detail: `No ${staff.employment_type === 'contractor' ? '1099' : 'W2'} offer letter template exists for ${role?.label || 'this role'} — one must be added before this can be sent` });
  } else if (!offerDoc) {
    items.push({ key: 'offer_letter', label: 'Offer Letter signed', state: 'missing', detail: 'Not yet sent' });
  } else if (offerDoc.status !== 'signed') {
    items.push({ key: 'offer_letter', label: 'Offer Letter signed', state: 'pending', editable: true,
      detail: originalNote ? `${offerDoc.doc_label} sent ${fmtDate(offerDoc.sent_at)}, awaiting signature${originalNote}` : 'Sent, awaiting candidate signature' });
  } else {
    items.push({ key: 'offer_letter', label: 'Offer Letter signed', state: 'complete', revisable: true,
      detail: `${originalNote ? `${offerDoc.doc_label} signed` : 'Signed'} ${fmtDate(offerDoc.signed_at)}${offerDoc.office_signed_by ? ` · countersigned by ${offerDoc.office_signed_by}` : ' · not countersigned'}${originalNote}` });
  }

  // Job description
  const jdDoc = findDoc('job_description');
  if (!jdDoc && noApplicationFlow) {
    items.push({ key: 'job_description', label: 'Job Description signed', state: 'na', detail: staff.source === 'axiscare' ? 'Added via AxisCare sync — no job description expected' : 'Added manually — no job description expected' });
  } else if (!jdDoc) {
    items.push({ key: 'job_description', label: 'Job Description signed', state: 'missing', detail: 'Not yet sent' });
  } else if (jdDoc.status !== 'signed') {
    items.push({ key: 'job_description', label: 'Job Description signed', state: 'pending', detail: 'Sent, awaiting candidate signature' });
  } else {
    items.push({ key: 'job_description', label: 'Job Description signed', state: 'complete', detail: `Signed ${fmtDate(jdDoc.signed_at)}${jdDoc.office_signed_by ? ` · countersigned by ${jdDoc.office_signed_by}` : ' · not countersigned'}` });
  }

  // Background check. For a nurse that is the license, recorded below with the
  // other checks AHFC runs itself.
  if (staff.bg_check_type !== 'license_verification') {
    if (staff.bg_check_status === 'cleared') {
      items.push({ key: 'bg_check', label: 'Background Check (GCHEXS)', state: 'complete', detail: `Cleared ${fmtDate(staff.bg_check_cleared_at)}` });
    } else if (staff.bg_check_status === 'flagged') {
      items.push({ key: 'bg_check', label: 'Background Check (GCHEXS)', state: 'flagged', detail: 'Unsatisfactory determination — review before assigning client care' });
    } else if (staff.bg_check_status === 'pending') {
      items.push({ key: 'bg_check', label: 'Background Check (GCHEXS)', state: 'pending', detail: `Application submitted ${fmtDate(staff.bg_check_initiated_at)}` });
    } else {
      items.push({ key: 'bg_check', label: 'Background Check (GCHEXS)', state: 'missing', detail: 'Not yet started' });
    }
  }

  // The checks AHFC runs itself: who looked, when, and the proof. A date with
  // nothing to show for it reads as pending, and exclusion screening falls due
  // again every month.
  screeningKindsFor(staff).forEach((kind) => {
    const c = screeningState(staff, kind, documents);
    const when = c.at ? `${fmtDate(c.at)}${c.by ? ` · ${c.by}` : ''}` : '';
    let detail;
    if (c.stale) detail = `Last checked ${when}, ${c.days} days ago. Due every ${EXCLUSION_STALE_DAYS} days at most.`;
    else if (!c.at && kind === 'license' && staff.bg_check_status === 'cleared') detail = `Marked cleared ${fmtDate(staff.bg_check_cleared_at)}, with no record of who verified it.`;
    else if (!c.at) detail = 'Not recorded';
    else detail = `${when}${c.proofs.length ? ` · ${c.proofs.length} proof file${c.proofs.length === 1 ? '' : 's'}` : ' · no proof attached'}`;
    items.push({ key: `screening_${kind}`, label: c.spec.label, state: c.state, detail, recordKind: kind });
  });

  // Background Check Consent — the signed authorization required before
  // GCHEXS/license verification can even begin (see bg_check item above).
  // Separate from that item because the two can be in different states —
  // e.g. consent signed but GCHEXS still pending, or vice versa for a
  // fast-tracked staff member with neither started.
  const consentDoc = findDoc('background_check_consent');
  if (!consentDoc) {
    items.push({ key: 'bg_check_consent', label: 'Background Check Consent signed', state: 'missing', detail: 'Not yet sent' });
  } else if (consentDoc.status !== 'signed') {
    items.push({ key: 'bg_check_consent', label: 'Background Check Consent signed', state: 'pending', detail: 'Sent, awaiting signature' });
  } else {
    items.push({ key: 'bg_check_consent', label: 'Background Check Consent signed', state: 'complete', detail: `Signed ${fmtDate(consentDoc.signed_at)}` });
  }

  // Clinical certs — only for direct-care roles (see needsClinicalCerts in OnboardingPipeline.js)
  if (['RN', 'LPN', 'PCA', 'BHA'].includes(staff.position_code)) {
    [{ type: 'cpr_first_aid', label: 'CPR / First Aid' }, { type: 'tb_screening', label: 'TB Screening' }].forEach(({ type, label }) => {
      const cert = findCert(type);
      if (!cert) { items.push({ key: type, label, state: 'missing', detail: 'Not on file' }); return; }
      const s = certificationStatus(cert);
      items.push({ key: type, label, state: s.key === 'expired' ? 'missing' : 'complete', detail: cert.expiry_date ? `Expires ${fmtDate(cert.expiry_date)}` : 'On file' });
    });
  } else {
    items.push({ key: 'clinical_certs', label: 'CPR/First Aid & TB Screening', state: 'na', detail: 'Not required for this role' });
  }

  // Compliance training (HIPAA/Code of Conduct/Handbook via POLICY-MANUAL + PERSONNEL-HANDBOOK)
  const policyManual = findCourse('POLICY-MANUAL');
  const handbook = findCourse('PERSONNEL-HANDBOOK');
  const bothDone = policyManual?.status === 'completed' && handbook?.status === 'completed';
  const eitherStarted = policyManual || handbook;
  items.push({
    key: 'compliance_training', label: 'HIPAA / Code of Conduct / Handbook Training',
    state: bothDone ? 'complete' : eitherStarted ? 'pending' : 'missing',
    detail: bothDone ? 'Policy Manual and Personnel Handbook both completed' : eitherStarted ? 'In progress' : 'Not yet enrolled',
  });

  // Orientation — since 2026-09-07 the new hire signs an attestation inside
  // the offer bundle; the office-filled checklist only exists on older files.
  const orientationDoc = findDoc('orientation_attestation') || findDoc('orientation_checklist');
  if (!orientationDoc && noApplicationFlow) {
    items.push({ key: 'orientation', label: 'Orientation attestation signed', state: 'na', detail: 'Added manually — no bundle sent' });
  } else {
    items.push({
      key: 'orientation', label: 'Orientation attestation signed',
      state: orientationDoc?.status === 'signed' ? 'complete' : orientationDoc ? 'pending' : 'missing',
      detail: orientationDoc?.status === 'signed' ? `Signed ${fmtDate(orientationDoc.signed_at)}` : orientationDoc ? 'Sent with the offer bundle, awaiting signature' : 'Not yet sent',
    });
  }

  // Policy acknowledgment + HIPAA/confidentiality — part of the bundle too.
  [['policy_acknowledgment', 'Policy Manual & Handbook acknowledgment signed'], ['confidentiality_agreement', 'HIPAA & Confidentiality agreement signed']].forEach(([type, label]) => {
    const d = findDoc(type);
    if (!d && noApplicationFlow) { items.push({ key: type, label, state: 'na', detail: 'Added manually — no bundle sent' }); return; }
    items.push({ key: type, label, state: d?.status === 'signed' ? 'complete' : d ? 'pending' : 'missing', detail: d?.status === 'signed' ? `Signed ${fmtDate(d.signed_at)}` : d ? 'Awaiting signature' : 'Not yet sent' });
  });

  // Gusto — payroll, tax forms (W-4/I-9 or W-9) and direct deposit all live
  // there. A follow-up, not a gate: the person can work before it is done,
  // but Payroll flags any line for someone with no Gusto confirmation.
  if ((staff.hire_track || staff.employment_type) === 'agency') {
    items.push({ key: 'gusto', label: 'Gusto payroll setup', state: 'na', detail: 'Agency worker — paid by the sending agency' });
  } else {
    items.push({ key: 'gusto', label: 'Gusto payroll setup (W-4 / I-9 / W-9, direct deposit)', state: staff.gusto_created_at ? 'complete' : 'missing', detail: staff.gusto_created_at ? `Confirmed ${fmtDate(staff.gusto_created_at)}` : 'Not yet confirmed — create the record in Gusto, then mark done on Training & Compliance › Onboarding follow-ups' });
  }

  return items;
}

const ChecklistPill = ({ state }) => {
  const s = CHECKLIST_STATE[state] || CHECKLIST_STATE.na;
  return <span style={{ fontSize:11, fontWeight:500, padding:'3px 10px', borderRadius:20, background:s.bg, color:s.color, whiteSpace:'nowrap' }}>{s.icon} {s.label}</span>;
};

export default function PersonnelProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isManager, isAdmin } = useRole();
  const { profile } = useAuth();

  const recordNav = useRecordNav({ table: 'staff', id, base: '/hr/personnel', stateKey: 'staffIds', filter: q => q.or('hidden.is.null,hidden.eq.false') });

  const [staff, setStaff] = useState(null);
  const [markingGusto, setMarkingGusto] = useState(false);
  // Same write Training & Compliance makes, plus an onboarding event so the
  // timeline shows who confirmed it and when.
  const markGustoDone = async () => {
    if (!staff) return;
    setMarkingGusto(true);
    const at = new Date().toISOString();
    const { error } = await supabase.from('staff').update({ gusto_created_at: at }).eq('id', staff.id);
    if (!error) {
      await supabase.from('onboarding_events').insert({
        staff_id: staff.id, event_type: 'gusto_created',
        event_detail: `Gusto set up — confirmed by ${profile?.full_name || 'staff'}`,
        previous_status: staff.onboarding_status || null, new_status: staff.onboarding_status || null,
        actor: profile?.full_name || 'staff',
      });
      setStaff((p) => ({ ...p, gusto_created_at: at }));
    } else {
      window.alert(`Could not save: ${error.message}`);
    }
    setMarkingGusto(false);
  };
  const [roles, setRoles] = useState([]);
  const [application, setApplication] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [supervision, setSupervision] = useState([]);
  useEffect(() => { if (!id) return; getStaffSupervisionHistory(id).then(({ data }) => setSupervision(data || [])); }, [id]);
  const [courseRecords, setCourseRecords] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [events, setEvents] = useState([]);
  const [performanceReviews, setPerformanceReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [viewingDoc, setViewingDoc] = useState(null);
  const [copiedTokenId, setCopiedTokenId] = useState(null);
  const [copyFailedTokenId, setCopyFailedTokenId] = useState(null);
  const [requestModal, setRequestModal] = useState(null); // { docType, docLabel }
  const [reviseOffer, setReviseOffer] = useState(null); // { signed }
  const [recordCheck, setRecordCheck] = useState(null); // a SCREENING_KINDS key
  const [docRequestMsg, setDocRequestMsg] = useState('');
  const [showGrantLogin, setShowGrantLogin] = useState(false);
  // Full SSN lives encrypted on the application (see migration
  // 20260907100000); reveal_application_ssn is admin-only and writes an
  // audit event. Shown for 30 seconds, never kept in state longer.
  const [revealedSsn, setRevealedSsn] = useState(null);
  const [ssnBusy, setSsnBusy] = useState(false);
  const revealSsn = async () => {
    if (!staff?.application_id) return;
    setSsnBusy(true);
    try {
      const { data, error } = await supabase.rpc('reveal_application_ssn', { p_application_id: staff.application_id });
      if (error) throw error;
      if (!data) { setRevealedSsn('none'); return; }
      setRevealedSsn(data);
      setTimeout(() => setRevealedSsn(null), 30000);
    } catch (err) { alert(`Could not reveal SSN: ${err.message}`); }
    finally { setSsnBusy(false); }
  };

  const load = useCallback(async () => {
    const { data: s } = await supabase
      .from('staff')
      .select('*, profiles(full_name, role, email, modules), supervisor:staff!supervisor_id(id, full_name, first_name, last_name), contracting_agency:contracting_agencies(id, name, status, signed_pdf_url, annual_review_due_at)')
      .eq('id', id)
      .single();
    setStaff(s);
    setLoading(false);
    if (!s) return;

    if (s.application_id) {
      supabase.from('staff_applications').select('*').eq('id', s.application_id).single()
        .then(({ data }) => setApplication(data || null));
    } else {
      setApplication(null);
    }
    supabase.from('staff_documents').select('*').eq('staff_id', id).order('created_at')
      .then(({ data }) => setDocuments(data || []));
    supabase.from('staff_certifications').select('*').eq('staff_id', id).order('expiry_date')
      .then(({ data }) => setCertifications(data || []));
    supabase.from('staff_course_records').select('*, course:training_courses(code, title, renewal_cadence)').eq('staff_id', id)
      .then(({ data }) => setCourseRecords(data || []));
    supabase.from('staff_incidents').select('*').eq('staff_id', id).order('incident_date', { ascending: false })
      .then(({ data }) => setIncidents(data || []));
    supabase.from('onboarding_events').select('*').eq('staff_id', id).order('created_at')
      .then(({ data }) => setEvents(data || []));
    if (isManager) {
      getStaffPerformanceReviews(id).then(({ data }) => setPerformanceReviews(data || []));
    }
  }, [id, isManager]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    supabase.from('roles').select('*').eq('active', true).order('label').then(({ data }) => setRoles(data || []));
  }, []);
  const roleLabel = code => roles.find(r => r.code === code)?.label || code;

  useEffect(() => {
    const handleFocus = () => load();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [load]);

  const copySignLink = async (doc) => {
    const link = `${window.location.origin}/hr-sign/${doc.sign_token}`;
    try {
      if (!navigator.clipboard) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(link);
      setCopiedTokenId(doc.id);
      setTimeout(() => setCopiedTokenId(null), 2000);
    } catch (err) {
      console.error('Copy sign link failed:', err);
      setCopyFailedTokenId(doc.id);
      setTimeout(() => setCopyFailedTokenId(null), 2000);
    }
  };

  if (loading) return <div className="empty-state"><div className="loading-spinner" style={{ margin:'0 auto' }} /></div>;
  if (!staff) return <div className="empty-state"><p>Personnel record not found.</p></div>;

  const pendingDocCount = documents.filter(d => d.status === 'pending' || d.status === 'sent').length;
  const openIncidentCount = incidents.filter(i => i.status === 'open').length;
  // certifications.status is a legacy unpopulated column — the real status
  // is always computed live from expiry_date/override fields (same helper
  // Training & Compliance uses), not read from a stored value.
  const expiredCertCount = certifications.filter(c => certificationStatus(c).key === 'expired').length;
  const supervisionOverdue = supervision.filter(v => v.supervised_staff_id === id && v.status === 'complete' && v.rn_cosign_due && !v.rn_countersigned_at && v.rn_cosign_due < new Date().toISOString().slice(0, 10)).length;
  const checklist = buildPersonnelChecklist(staff, application, documents, certifications, courseRecords);
  const checklistMissingCount = checklist.filter(i => i.state === 'missing' || i.state === 'flagged').length;

  const PROFILE_TABS = [
    { key: 'overview',       label: 'Overview' },
    { key: 'checklist',      label: 'Personnel File', badge: checklistMissingCount },
    { key: 'application',    label: 'Application' },
    { key: 'documents',      label: 'Documents', badge: pendingDocCount },
    { key: 'certifications', label: 'Certifications', badge: expiredCertCount },
    { key: 'training',       label: 'Training' },
    { key: 'supervision',    label: 'Supervision', badge: supervisionOverdue },
    { key: 'incidents',      label: 'Incidents', badge: openIncidentCount },
    { key: 'timeline',       label: 'Onboarding Timeline' },
    ...(isManager ? [{ key: 'performance', label: 'Performance' }] : []),
  ];

  const rc = roleColor(staff.position_code);
  const sc = statusColor(staff.status);
  const initials = staff.full_name?.split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

  return (
    <div>
      {/* Navigation bar */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, flexWrap:'wrap' }}>
        <span style={{ fontSize:11, color:'var(--text3)', display:'flex', alignItems:'center', gap:4 }}>
          <span style={{ color:'var(--accent)', cursor:'pointer' }} onClick={() => navigate('/hr/staff-records')}>Personnel</span>
          <span>/</span>
          <span>{staff.last_name ? `${staff.last_name}, ${staff.first_name}` : staff.full_name}</span>
        </span>
        <div style={{ flex:1 }} />
        <RecordNav {...recordNav} label="person" />
      </div>

      {/* Header */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ padding:24, display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
          <div style={{ width:56, height:56, borderRadius:'50%', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:600, color:'#fff', flexShrink:0 }}>
            {initials}
          </div>
          <div style={{ flex:1, minWidth:200 }}>
            <div style={{ fontSize:20, fontWeight:600, color:'var(--text)' }}>
              {staff.last_name}, {staff.first_name}{staff.suffix ? ` ${staff.suffix}` : ''}
              {staff.goes_by && <span style={{ fontSize:14, fontWeight:400, color:'var(--text3)' }}> "{staff.goes_by}"</span>}
            </div>
            <div style={{ fontSize:12, color:'var(--text3)', marginTop:3 }}>
              {staff.employee_id
                ? <span style={{ fontFamily:'DM Mono, monospace' }}>{staff.employee_id}</span>
                : <span style={{ fontStyle:'italic' }} title="Assigned once the offer is signed">Employee ID pending</span>}
              {' · '}{staff.position_code ? roleLabel(staff.position_code) + (staff.is_lead ? ' (Lead)' : '') : 'Position pending'}
              {staff.department && <> · {staff.department}</>}
              {staff.start_date && <> · Since {fmtDate(staff.start_date)}</>}
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            {pill(staff.position_code ? roleLabel(staff.position_code) : 'Pending', { background: rc.bg, color: rc.color, fontSize:12, padding:'5px 12px' })}
            {pill(staff.status?.replace('_',' '), { background: sc.bg, color: sc.color, fontSize:12, padding:'5px 12px' })}
            {staff.source === 'axiscare'
              ? pill('AxisCare', { background: 'var(--info-bg)', color: 'var(--info)', fontSize:12, padding:'5px 12px' })
              : application
                ? pill('From Application', { background: 'var(--accent-bg)', color: 'var(--accent)', fontSize:12, padding:'5px 12px' })
                : pill('Manual', { background: 'var(--bg2)', color: 'var(--text3)', fontSize:12, padding:'5px 12px' })}
            {staff.fast_tracked_at && pill(`⚡ Fast-Tracked ${fmtDate(staff.fast_tracked_at)}`, { background: 'var(--warn-bg)', color: 'var(--warn)', fontSize:12, padding:'5px 12px' })}
            {checklistMissingCount > 0 && (
              <span title="See the Personnel File tab" style={{ cursor:'pointer' }} onClick={() => setActiveTab('checklist')}>
                {pill(`${checklistMissingCount} item${checklistMissingCount === 1 ? '' : 's'} need attention`, { background: 'var(--danger-bg)', color: 'var(--danger)', fontSize:12, padding:'5px 12px' })}
              </span>
            )}
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/hr/staff-records', { state: { editId: staff.id } })}>Edit</button>
          </div>
        </div>
      </div>

      {/* Gusto is a follow-up, never a gate — the person is already active and
          in AxisCare by now. But a new hire wants to know they will be paid
          before their first shift, and a small red pill was too easy to read
          past, so until it is done it gets the full width of the page. The
          office is also told in Chat at the moment of activation. Agency
          placements are paid by their agency and never see this. */}
      {staff.status === 'active' && !staff.gusto_created_at && (staff.hire_track || staff.employment_type) !== 'agency' && (
        <div style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap', margin:'0 0 16px', padding:'14px 18px', borderRadius:12,
          background:'var(--warn-bg)', border:'1.5px solid var(--warn)' }}>
          <span aria-hidden style={{ fontSize:22 }}>💼</span>
          <div style={{ flex:1, minWidth:220 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>Gusto not set up yet</div>
            <div style={{ fontSize:12.5, color:'var(--text2)' }}>
              Not in payroll yet, so there's no confirmation of pay before the first shift. Add {staff.first_name || 'this person'} to Gusto as a {staff.employment_type === 'contractor' ? '1099 contractor' : 'W2 employee'} — Gusto emails them for tax forms and direct deposit — then mark it done here.
            </div>
          </div>
          <button className="btn btn-primary btn-sm" disabled={markingGusto} onClick={markGustoDone}>
            {markingGusto ? 'Saving…' : 'Mark Gusto set up'}
          </button>
        </div>
      )}

      <ProfileTabBar tabs={PROFILE_TABS} activeKey={activeTab} onSelect={setActiveTab} mobileVisible={4} />

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="grid-2">
          <div>
            <Section title="Contact & Employment">
              <Detail label="Email" value={staff.email} />
              <Detail label="Phone" value={staff.phone} />
              <Detail label="Position" value={staff.position_code ? roleLabel(staff.position_code) + (staff.is_lead ? ' (Lead)' : '') : 'Not yet assigned'} />
              {staff.position_code === 'PCA' && (
                <Detail label="Caregiver Relationship" value={staff.caregiver_relationship === 'family' ? 'Family Caregiver' : staff.caregiver_relationship === 'independent' ? 'Independent' : '—'} />
              )}
              {staff.is_administrator_of_record && <Detail label="Administrator of Record" value="Yes" />}
              <Detail label="Employment Type" value={staff.employment_type?.replace('_',' ')} />
              <Detail
                label="Contracting Agency"
                value={staff.contracting_agency
                  ? `${staff.contracting_agency.name}${staff.contracting_agency.signed_pdf_url ? ` — agreement on file${staff.contracting_agency.annual_review_due_at ? `, review due ${fmtDate(staff.contracting_agency.annual_review_due_at)}` : ''}` : ' — no signed agreement on file'}`
                  : staff.employment_type === 'agency'
                    ? 'Not set — marked as an agency worker but no agency linked'
                    : 'Direct — set Employment Type to “Agency” in Edit to link one'}
              />
              <Detail label={staff.employment_type === 'agency' ? 'Agency Bill Rate' : 'Pay Rate'} value={payRate(staff)} />
              <Detail label="Start Date" value={fmtDate(staff.start_date)} />
              <Detail label="Department" value={staff.department} />
              <Detail label="Supervisor" value={staff.supervisor?.full_name || staff.supervisor_name} />
              <Detail label="Onboarding Status" value={staff.onboarding_status?.replace('_',' ')} />
            </Section>
            {/* What this person is paid from which date. Payroll pays each
                visit at the rate in force on its date. Managers only, like
                payroll itself. */}
            {isManager && (
              <Section title="Pay Rate History">
                <PayRateHistory staff={staff} createdBy={profile?.id} onChanged={() => load()} />
              </Section>
            )}
            {staff.status === 'terminated' && (
              <Section title="Termination">
                <Detail label="Date" value={fmtDate(staff.termination_date)} />
                <Detail label="Rehire Eligible" value={staff.rehire_eligible === true ? 'Yes' : staff.rehire_eligible === false ? 'No' : 'Not determined'} />
                <Detail label="Reason" value={staff.termination_reason} />
              </Section>
            )}
            <Section title="Background Check">
              <Detail label="Type" value={staff.bg_check_type === 'gchexs' ? 'GCHEXS' : staff.bg_check_type === 'license_verification' ? 'License Verification' : staff.bg_check_type} />
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'0.5px solid var(--border)', gap:12 }}>
                <span style={{ fontSize:12, color:'var(--text3)' }}>Status</span>
                {staff.bg_check_status ? pill(staff.bg_check_status.replace('_',' '), (() => {
                  const m = { not_started:{bg:'var(--bg2)',color:'var(--text3)'}, pending:{bg:'var(--warn-bg)',color:'var(--warn)'}, cleared:{bg:'var(--success-bg)',color:'var(--success)'}, flagged:{bg:'var(--danger-bg)',color:'var(--danger)'} };
                  return m[staff.bg_check_status] || { bg:'var(--bg2)', color:'var(--text3)' };
                })()) : '—'}
              </div>
              <Detail label="Initiated" value={fmtDate(staff.bg_check_initiated_at, true)} />
              <Detail label="Cleared" value={fmtDate(staff.bg_check_cleared_at, true)} />
              <div style={{ padding:'12px 0 4px' }}>
                <BackgroundCheckPanel staff={staff} onUpdated={() => load()} />
              </div>
            </Section>
            <Section title="Screening We Run Ourselves">
              <ScreeningChecksPanel staff={staff} documents={documents} onRecord={setRecordCheck} />
            </Section>
          </div>
          <div>
            <Section title="Personal Info">
              <Detail label="Date of Birth" value={fmtDate(staff.date_of_birth)} />
              <Detail label="Cannot work around" value={staff.pet_restrictions?.length
                ? [RESTRICTION_OPTIONS.filter(([k]) => staff.pet_restrictions.includes(k)).map(([, l]) => l).join(', '), staff.pet_restrictions_notes].filter(Boolean).join(' · ')
                : (staff.pet_restrictions_notes || '—')} />
              <Detail label={revealedSsn && revealedSsn !== 'none' ? 'SSN (revealed, 30s)' : 'SSN (last 4)'} value={
                revealedSsn === 'none'
                  ? <span style={{ color: 'var(--text3)' }}>Not on the application</span>
                  : revealedSsn
                    ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{revealedSsn.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3')} <button onClick={() => setRevealedSsn(null)} style={{ marginLeft: 8, fontSize: 11, background: 'none', border: 'none', color: 'var(--accent2)', cursor: 'pointer', padding: 0 }}>Hide</button></span>
                    : <span>{staff.ssn_last4 ? `••• •• ${staff.ssn_last4}` : '—'}{isAdmin && staff.application_id && (
                        <button onClick={revealSsn} disabled={ssnBusy} title="Admin only — every reveal is logged on the timeline" style={{ marginLeft: 8, fontSize: 11, background: 'none', border: '0.5px solid var(--border2)', borderRadius: 6, padding: '2px 8px', color: 'var(--accent2)', cursor: 'pointer', fontFamily: 'inherit' }}>{ssnBusy ? 'Revealing…' : 'Reveal'}</button>
                      )}</span>
              } />
              <Detail label="Gender" value={staff.gender} />
              <Detail label="Preferred Language" value={staff.preferred_language === 'es' ? 'Spanish' : 'English'} />
            </Section>
            <Section title="Address">
              <Detail label="Street" value={staff.address} />
              <Detail label="City" value={staff.city} />
              <Detail label="County" value={staff.county} />
              <Detail label="State" value={staff.state} />
              <Detail label="ZIP" value={staff.zip} />
            </Section>
            <Section title="Emergency Contact">
              <Detail label="Name" value={staff.emergency_contact_name} />
              <Detail label="Phone" value={staff.emergency_contact_phone} />
              <Detail label="Relationship" value={staff.emergency_contact_relationship} />
            </Section>
            <Section title="System" action={isAdmin && !staff.profile_id && staff.status !== 'terminated' && (
              <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => setShowGrantLogin(true)}>Grant CAERA login</button>
            )}>
              <Detail label="Source" value={staff.source === 'axiscare' ? 'AxisCare' : 'Manual'} />
              {staff.axiscare_id && <Detail label="AxisCare ID" value={staff.axiscare_id} />}
              <Detail label="CAERA Login" value={staff.profile_id ? `${staff.profiles?.full_name || 'Linked'} (${staff.profiles?.role || '—'})` : 'No login'} />
              <Detail label="Notes" value={staff.notes} />
            </Section>
          </div>
        </div>
      )}

      {/* Personnel File Checklist */}
      {activeTab === 'checklist' && (
        <div className="card" style={{ border:'0.5px solid var(--accent-border)' }}>
          <div className="card-header" style={{ background:'var(--accent-bg)', borderBottom:'1px solid var(--accent-border)' }}>
            <div className="card-title" style={{ color:'var(--accent)' }}>Personnel File Completeness</div>
          </div>
          {docRequestMsg && <div style={{ margin:'12px 20px 0', fontSize:12, color:'var(--success)', background:'var(--success-bg)', border:'1px solid var(--success-border)', borderRadius:8, padding:'8px 12px' }}>{docRequestMsg}</div>}
          <div style={{ padding:'0 20px' }}>
            {checklist.map((item, i) => {
              const docType = DOC_TYPE_FOR_CHECKLIST_KEY[item.key];
              const canRequest = docType && (item.state === 'missing' || item.state === 'pending');
              return (
                <div key={item.key} style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, padding:'14px 0', borderBottom: i < checklist.length-1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:500, color:'var(--text)' }}>{item.label}</div>
                    <div style={{ fontSize:12, color:'var(--text3)', marginTop:2 }}>{item.detail}</div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                    {item.key === 'offer_letter' && (item.editable || (item.revisable && canReviseSigned(staff))) && (
                      <button className="btn btn-outline btn-sm" style={{ fontSize:11 }}
                        onClick={() => { setDocRequestMsg(''); setReviseOffer({ signed: !item.editable }); }}>
                        {item.editable ? 'Edit & resend' : 'Revise & resend'}
                      </button>
                    )}
                    {item.recordKind && (
                      <button className="btn btn-outline btn-sm" style={{ fontSize:11 }} onClick={() => setRecordCheck(item.recordKind)}>
                        {item.state === 'missing' && !staff[SCREENING_KINDS[item.recordKind].atField] ? 'Record' : 'Record again'}
                      </button>
                    )}
                    {canRequest && (
                      <button className="btn btn-outline btn-sm" style={{ fontSize:11 }}
                        onClick={() => { setDocRequestMsg(''); setRequestModal({ docType, docLabel: item.label.replace(' signed', '') }); }}>
                        {item.state === 'pending' ? 'Resend' : 'Send request'}
                      </button>
                    )}
                    <ChecklistPill state={item.state} />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ padding:'12px 20px', fontSize:11, color:'var(--text3)', borderTop:'1px solid var(--border)' }}>
            {checklistMissingCount === 0
              ? '✓ Nothing outstanding (N/A and External items are not required here).'
              : `${checklistMissingCount} item${checklistMissingCount === 1 ? '' : 's'} missing or flagged.`}
          </div>
        </div>
      )}

      {recordCheck && (
        <RecordCheckModal
          staff={staff}
          kind={recordCheck}
          onClose={() => setRecordCheck(null)}
          onRecorded={() => {
            setDocRequestMsg(`${SCREENING_KINDS[recordCheck].label} recorded.`);
            setTimeout(() => setDocRequestMsg(''), 5000);
            setRecordCheck(null);
            load();
          }}
        />
      )}

      {showGrantLogin && (
        <GrantLoginModal
          staff={staff}
          onClose={() => setShowGrantLogin(false)}
          onDone={() => { setShowGrantLogin(false); load(); }}
        />
      )}

      {reviseOffer && (
        <ReviseOfferModal
          staff={staff}
          signed={reviseOffer.signed}
          onClose={() => setReviseOffer(null)}
          onDone={({ mode, axisNote }) => {
            setReviseOffer(null);
            setDocRequestMsg(`${mode === 'revised' ? 'Revised offer letter' : 'Updated offer letter'} sent to ${staff.full_name}.${axisNote ? ` ${axisNote}` : ''}`);
            setTimeout(() => setDocRequestMsg(''), 8000);
            load();
          }}
        />
      )}

      {requestModal && (
        <RequestDocModal
          staff={staff}
          docType={requestModal.docType}
          docLabel={requestModal.docLabel}
          officeSignedBy={profile?.full_name}
          officeSignerRole={profile?.role}
          onClose={() => setRequestModal(null)}
          onSent={() => { setRequestModal(null); setDocRequestMsg(`Sent to ${staff.full_name}.`); setTimeout(() => setDocRequestMsg(''), 5000); load(); }}
        />
      )}

      {/* Application */}
      {activeTab === 'application' && (
        <div>
          {!application ? (
            <div className="card"><div className="empty-state">
              <p style={{ fontSize:14, color:'var(--text2)', fontWeight:500 }}>No application on file</p>
              <p style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>This record was added manually or synced from AxisCare, not through the Apply Form.</p>
            </div></div>
          ) : (
            <div className="grid-2">
              <div>
                <Section title="Application Details" action={pill(application.status, applicationStatusColor(application.status))}>
                  <Detail label="Applicant Name" value={`${application.first_name || ''} ${application.last_name || ''}`.trim()} />
                  <Detail label="Email" value={application.email} />
                  <Detail label="Phone" value={application.phone} />
                  <Detail label="Date of Birth" value={fmtDate(application.date_of_birth)} />
                  <Detail label="Address" value={application.address} />
                  <Detail label="City / State / ZIP" value={[application.city, [application.state, application.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ') || null} />
                  <Detail label="Position Applied For" value={application.position_applied_for} />
                  <Detail label="Employment Type Desired" value={application.employment_type_desired} />
                </Section>
                <Section title="Work History">
                  <div style={{ padding:'10px 0' }}>
                    <p style={{ fontSize:13, color:'var(--text)', lineHeight:1.6, whiteSpace:'pre-wrap' }}>{application.work_history || 'Not provided'}</p>
                  </div>
                </Section>
              </div>
              <div>
                <Section title="Submitted Documents">
                  <div style={{ padding:'10px 0', display:'flex', flexDirection:'column', gap:8 }}>
                    {application.resume_url && <a href={application.resume_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm" style={{ width:'fit-content' }}>Resume ↗</a>}
                    {application.id_document_url && <a href={application.id_document_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm" style={{ width:'fit-content' }}>ID Document ↗</a>}
                    {(application.certification_urls || []).map((c, i) => (
                      <a key={i} href={c.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm" style={{ width:'fit-content' }}>{c.label || 'Certification'} ↗</a>
                    ))}
                    {!application.resume_url && !application.id_document_url && !(application.certification_urls || []).length && (
                      <span style={{ fontSize:12, color:'var(--text3)' }}>No documents submitted</span>
                    )}
                  </div>
                </Section>
                <Section title="Consent & Review">
                  <Detail label="Consent Signed By" value={application.consent_signature_name} />
                  <Detail label="Consent Signed At" value={fmtDate(application.consent_signed_at, true)} />
                  <Detail label="Reviewed By" value={application.reviewed_by} />
                  <Detail label="Reviewed At" value={fmtDate(application.reviewed_at, true)} />
                  {application.rejection_reason && <Detail label="Rejection Reason" value={application.rejection_reason} />}
                </Section>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Documents */}
      {activeTab === 'documents' && (
        <div>
          <StaffFilesPanel staff={staff} documents={documents} onChanged={load} />
        <div className="card" style={{ border:'0.5px solid var(--accent-border)' }}>
          <div className="card-header" style={{ background:'var(--accent-bg)', borderBottom:'1px solid var(--accent-border)' }}>
            <div className="card-title" style={{ color:'var(--accent)' }}>Signed &amp; Recorded</div>
          </div>
          {documents.length === 0 ? (
            <div className="empty-state"><p>No documents on file yet.</p></div>
          ) : (
            <div style={{ padding:'0 20px' }}>
              {documents.map((doc, i) => (
                <div key={doc.id} style={{ display:'flex', alignItems:'flex-start', gap:16, padding:'14px 0', borderBottom: i < documents.length-1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:4 }}>
                      <span style={{ fontSize:14, fontWeight:500, color:'var(--text)' }}>{doc.doc_label || doc.doc_type}</span>
                      {pill(doc.status, docStatusColor(doc.status))}
                    </div>
                    <div style={{ fontSize:11, color:'var(--text3)' }}>
                      {doc.signed_at && <>Signed by {doc.signed_name || '—'} · {fmtDate(doc.signed_at, true)}</>}
                      {!doc.signed_at && doc.sent_at && <>{doc.status === 'on_file' ? 'On file' : 'Sent'} {fmtDate(doc.sent_at, true)}</>}
                      {!doc.signed_at && !doc.sent_at && <>Created {fmtDate(doc.created_at, true)}</>}
                    </div>
                    {doc.office_signed_by && (
                      <div style={{ fontSize:11, color:'var(--text3)' }}>
                        Countersigned by {doc.office_signed_by}{doc.office_signer_role ? ` (${doc.office_signer_role})` : ''} · {fmtDate(doc.office_signed_at, true)}
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                    {doc.content && <button className="btn btn-outline btn-sm" style={{ fontSize:12 }} onClick={() => setViewingDoc(doc)}>View</button>}
                    {doc.pdf_url && <a href={doc.pdf_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm" style={{ fontSize:12 }}>PDF ↗</a>}
                    {doc.sign_token && doc.status !== 'signed' && (
                      <button className="btn btn-outline btn-sm" style={{ fontSize:12, color: copyFailedTokenId === doc.id ? 'var(--danger)' : undefined }} onClick={() => copySignLink(doc)}>
                        {copiedTokenId === doc.id ? 'Copied!' : copyFailedTokenId === doc.id ? 'Copy failed' : 'Copy Sign Link'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      )}

      {/* Certifications */}
      {activeTab === 'certifications' && (
        <div>
          {/* Actionable CPR/TB tracking — request, remind, or mark on file.
              Previously only reachable from the Onboarding Pipeline, which
              excludes active staff entirely — see StaffCompliancePanels.js. */}
          <div className="card" style={{ border:'0.5px solid var(--accent-border)', marginBottom:12 }}>
            <div className="card-header" style={{ background:'var(--accent-bg)', borderBottom:'1px solid var(--accent-border)' }}>
              <div className="card-title" style={{ color:'var(--accent)' }}>Clinical Onboarding Requirements</div>
            </div>
            <div style={{ padding:16 }}>
              <ClinicalCertsPanel staff={staff} />
            </div>
          </div>
        <div className="card" style={{ border:'0.5px solid var(--accent-border)' }}>
          <div className="card-header" style={{ background:'var(--accent-bg)', borderBottom:'1px solid var(--accent-border)' }}>
            <div className="card-title" style={{ color:'var(--accent)' }}>All Certifications</div>
          </div>
          {certifications.length === 0 ? (
            <div className="empty-state"><p>No certifications on file.</p></div>
          ) : (
            <div style={{ padding:'0 20px' }}>
              {certifications.map((c, i) => (
                <div key={c.id} style={{ padding:'14px 0', borderBottom: i < certifications.length-1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:6 }}>
                    <span style={{ fontSize:14, fontWeight:500, color:'var(--text)' }}>{c.cert_label || c.cert_type}</span>
                    {pill(certificationStatus(c).label, certificationStatus(c))}
                    {c.verified && pill('Verified', { background:'var(--success-bg)', color:'var(--success)' })}
                    {c.override_active && pill('Override Active', { background:'var(--warn-bg)', color:'var(--warn)' })}
                    {c.verifying_party === 'sending_agency' && pill('From sending agency', { background:'var(--copper-bg)', color:'var(--copper)' })}
                  </div>
                  <div style={{ display:'flex', gap:20, flexWrap:'wrap', fontSize:12, color:'var(--text3)' }}>
                    {c.cert_number && <span>Number: {c.cert_number}</span>}
                    {c.issued_date && <span>Issued: {fmtDate(c.issued_date)}</span>}
                    {c.expiry_date && <span>Expires: {expiryValue(c.expiry_date)}</span>}
                  </div>
                  {c.notes && <div style={{ fontSize:12, color:'var(--text2)', marginTop:4 }}>{c.notes}</div>}
                  {c.document_url && <a href={c.document_url} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'var(--accent)' }}>View document ↗</a>}
                </div>
              ))}
            </div>
          )}
        </div>
        </div>
      )}

      {/* Training */}
      {activeTab === 'supervision' && (
        <div className="card">
          <div className="card-header"><div className="card-title">Supervisory visits</div><span style={{ fontSize: 11, color: 'var(--text3)' }}>Policy A-3.7 · kept in the personnel file and the client record</span></div>
          {supervision.length === 0 ? <div className="empty-state"><p>No supervisory visits on record.</p></div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['Date', 'Role', 'Client', 'Supervision of', 'By', 'Competent', 'Per plan', 'RN co-sign', 'Status'].map(h => <th key={h} style={{ textAlign: 'left', fontSize: 10, color: 'var(--text3)', padding: '6px 10px', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {supervision.map(v => (
                    <tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/hh/supervisor-visits/${v.id}`)}>
                      <td style={{ padding: '6px 10px', fontSize: 12 }}>{v.visit_date ? format(parseLocalDate(v.visit_date), 'MMM d, yyyy') : '—'}</td>
                      <td style={{ padding: '6px 10px', fontSize: 12 }}>{v.supervised_staff_id === id ? 'Supervised' : 'Supervising'}</td>
                      <td style={{ padding: '6px 10px', fontSize: 12 }}>{v.client_name || '—'}</td>
                      <td style={{ padding: '6px 10px', fontSize: 12 }}>{SUPERVISION_KIND[v.subject_kind] || v.subject_kind}</td>
                      <td style={{ padding: '6px 10px', fontSize: 12 }}>{v.supervisor_name || '—'}{v.supervising_credential ? ` (${v.supervising_credential})` : ''}</td>
                      <td style={{ padding: '6px 10px', fontSize: 12 }}>{v.competent == null ? '—' : v.competent ? 'Yes' : 'No'}</td>
                      <td style={{ padding: '6px 10px', fontSize: 12 }}>{v.care_per_plan == null ? '—' : v.care_per_plan ? 'Yes' : 'No'}</td>
                      <td style={{ padding: '6px 10px', fontSize: 12 }}>{v.rn_cosign_due ? (v.rn_countersigned_at ? '✓' : <span style={{ color: 'var(--warn)' }}>due {format(parseLocalDate(v.rn_cosign_due), 'MMM d')}</span>) : '—'}</td>
                      <td style={{ padding: '6px 10px', fontSize: 12 }}>{v.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {activeTab === 'training' && (
        <div className="card" style={{ border:'0.5px solid var(--accent-border)' }}>
          <div className="card-header" style={{ background:'var(--accent-bg)', borderBottom:'1px solid var(--accent-border)' }}>
            <div className="card-title" style={{ color:'var(--accent)' }}>Training & Learning Paths</div>
          </div>
          {courseRecords.length === 0 ? (
            <div className="empty-state"><p>No training courses assigned.</p></div>
          ) : (
            <div style={{ padding:'0 20px' }}>
              {courseRecords.map((r, i) => (
                <div key={r.id} style={{ display:'flex', alignItems:'flex-start', gap:16, padding:'14px 0', borderBottom: i < courseRecords.length-1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:4 }}>
                      <span style={{ fontSize:14, fontWeight:500, color:'var(--text)' }}>{r.course?.title || 'Course'}</span>
                      {pill(r.status?.replace('_',' '), courseStatusColor(r.status))}
                    </div>
                    <div style={{ fontSize:11, color:'var(--text3)' }}>
                      Assigned {fmtDate(r.assigned_at, true)}
                      {r.completed_at && <> · Completed {fmtDate(r.completed_at, true)}</>}
                    </div>
                  </div>
                  {r.pdf_url && <a href={r.pdf_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm" style={{ fontSize:12, flexShrink:0 }}>Certificate ↗</a>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Incidents */}
      {activeTab === 'incidents' && (
        <div className="card" style={{ border:'0.5px solid var(--accent-border)' }}>
          <div className="card-header" style={{ background:'var(--accent-bg)', borderBottom:'1px solid var(--accent-border)' }}>
            <div className="card-title" style={{ color:'var(--accent)' }}>Incident Log</div>
          </div>
          {incidents.length === 0 ? (
            <div className="empty-state"><p>No incidents on file.</p></div>
          ) : (
            <div style={{ padding:'0 20px' }}>
              {incidents.map((inc, i) => (
                <div key={inc.id} style={{ padding:'14px 0', borderBottom: i < incidents.length-1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:6 }}>
                    <span style={{ fontSize:14, fontWeight:500, color:'var(--text)' }}>{inc.category?.replace('_',' ')}</span>
                    {pill(inc.status, incidentStatusColor(inc.status))}
                    <span style={{ fontSize:11, color:'var(--text3)' }}>{fmtDate(inc.incident_date)}</span>
                  </div>
                  <div style={{ fontSize:13, color:'var(--text2)', marginBottom:4 }}>{inc.description}</div>
                  {inc.action_taken && <div style={{ fontSize:12, color:'var(--text3)' }}>Action taken: {inc.action_taken}</div>}
                  {inc.follow_up_date && <div style={{ fontSize:12, color:'var(--text3)' }}>Follow-up due: {fmtDate(inc.follow_up_date)}</div>}
                  {inc.staff_acknowledged && <div style={{ fontSize:11, color:'var(--success)', marginTop:4 }}>✓ Acknowledged by {inc.acknowledged_name} · {fmtDate(inc.acknowledged_at, true)}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Onboarding Timeline */}
      {activeTab === 'timeline' && (
        <div className="card">
          <div className="card-header"><div className="card-title">Onboarding Timeline</div></div>
          {events.length === 0 ? (
            <div className="empty-state"><p>No onboarding events recorded.</p></div>
          ) : (
            <div style={{ padding:'0 20px' }}>
              {events.map((ev, i) => (
                <div key={ev.id} style={{ display:'flex', gap:16, padding:'14px 0', borderBottom: i < events.length-1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div style={{ width:10, height:10, borderRadius:'50%', background:'var(--accent)', marginTop:3, flexShrink:0 }} />
                    {i < events.length-1 && <div style={{ width:1, flex:1, background:'var(--border)' }} />}
                  </div>
                  <div style={{ flex:1, paddingBottom:4 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                      <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{ev.event_type?.replace(/_/g,' ')}</span>
                      {ev.previous_status && ev.new_status && (
                        <span style={{ fontSize:11, color:'var(--text3)' }}>{ev.previous_status} → {ev.new_status}</span>
                      )}
                    </div>
                    {ev.event_detail && <div style={{ fontSize:13, color:'var(--text2)', marginBottom:4 }}>{ev.event_detail}</div>}
                    <div style={{ fontSize:11, color:'var(--text3)' }}>{ev.actor || 'System'} · {fmtDate(ev.created_at, true)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Performance — manager+ only, tab isn't even listed otherwise */}
      {activeTab === 'performance' && isManager && (
        <div className="card" style={{ border:'0.5px solid var(--accent-border)' }}>
          <div className="card-header" style={{ background:'var(--accent-bg)', borderBottom:'1px solid var(--accent-border)' }}>
            <div className="card-title" style={{ color:'var(--accent)' }}>Performance Reviews</div>
          </div>
          {performanceReviews.length === 0 ? (
            <div className="empty-state"><p>No performance reviews on file.</p></div>
          ) : (
            <div style={{ padding:'0 20px' }}>
              {performanceReviews.map((r, i) => (
                <div key={r.id} style={{ padding:'14px 0', borderBottom: i < performanceReviews.length-1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:6 }}>
                    <span style={{ fontSize:14, fontWeight:500, color:'var(--text)' }}>{REVIEW_TYPE_LABEL[r.review_type] || r.review_type}</span>
                    {pill(r.status, reviewStatusColor(r.status))}
                    {r.overall_rating != null && pill(`${r.overall_rating}/5`, { background:'var(--accent-bg)', color:'var(--accent)' })}
                    <span style={{ fontSize:11, color:'var(--text3)' }}>{fmtDate(r.review_date)}</span>
                  </div>
                  <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:6 }}>
                    {REVIEW_COMPETENCIES.map(c => r.ratings?.[c.key] ? (
                      <span key={c.key} style={{ fontSize:11, color:'var(--text3)' }}>{c.label}: <strong style={{ color:'var(--text)' }}>{r.ratings[c.key]}/5</strong></span>
                    ) : null)}
                  </div>
                  {r.strengths && <div style={{ fontSize:12, color:'var(--text2)', marginBottom:4 }}><strong>Strengths:</strong> {r.strengths}</div>}
                  {r.areas_for_improvement && <div style={{ fontSize:12, color:'var(--text2)', marginBottom:4 }}><strong>Areas for Improvement:</strong> {r.areas_for_improvement}</div>}
                  {r.goals_next_period && <div style={{ fontSize:12, color:'var(--text2)', marginBottom:4 }}><strong>Goals:</strong> {r.goals_next_period}</div>}
                  {r.staff_acknowledged && <div style={{ fontSize:11, color:'var(--success)', marginTop:4 }}>✓ Acknowledged by {r.signed_name || '—'} · {fmtDate(r.signed_at)}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* View document content modal */}
      {viewingDoc && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setViewingDoc(null)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth:760, width:'90vw' }}>
            <div className="modal-header">
              <div className="modal-title">{viewingDoc.doc_label || viewingDoc.doc_type}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setViewingDoc(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding:0 }}>
              <iframe
                title="document-content"
                srcDoc={viewingDoc.content || '<p style="font-family:sans-serif;color:#888;padding:20px">No content stored for this document.</p>'}
                style={{ width:'100%', height:'65vh', border:'none', background:'#fff' }}
                sandbox=""
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setViewingDoc(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
